import { queryOptions, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CalendarRange, History, Info, PartyPopper, Layers } from "lucide-react";
import {
  LOCK_AFTER_WEEKS,
  TIMESHEET_STATUS_LABEL,
  addDays,
  computeTotal,
  formatDay,
  fromYmd,
  hours as formatHours,
  isoWeek,
  nswPublicHoliday,
  periodHasWeekday,
  periodRange,
  startOfWeek,
  weekDays,
  weekRange,
  ymd,
  type AssignmentDTO,
  type MyTimesheetRow,
} from "@contractorhub/shared";
import { Button, Card, CardHeader, Skeleton, StatusBadge, cn } from "../ui";
import { myTimesheetsQuery } from "../../lib/queries";
import type { Row } from "./types";
import { isHalfWeek, rowKey } from "./period";

/** How many weeks of history the rail offers as a way back. */
const RECENT_WEEKS = 6;

/**
 * The weeks up to and including `monday`. The rail lists them as a way back, and
 * the route loads them before the editor mounts, because the editor picks the
 * project a fresh week opens on from them (TIME-14). One query, so one fetch.
 */
export function recentTimesheetsQuery(monday: Date) {
  return queryOptions({
    ...myTimesheetsQuery({
      from: ymd(addDays(monday, -7 * (RECENT_WEEKS - 1))),
      to: ymd(monday),
    }),
    staleTime: 60 * 1000,
  });
}

/**
 * A project this period is still waiting on: an engagement the contractor can
 * file against, with nothing worked on it and nothing submitted. These are what
 * the chase list asks about, so the rail names them — with the nil timesheet
 * (TIME-15) as the answer for one that genuinely had no work.
 */
function isOwed(row: Row): boolean {
  return (
    row.editable &&
    row.status !== "SUBMITTED" &&
    row.status !== "APPROVED" &&
    computeTotal(row.entries) === 0 &&
    periodHasWeekday({ periodStart: fromYmd(row.periodStart), periodEnd: fromYmd(row.periodEnd) })
  );
}

const DATE = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/** An ISO instant as a plain business-facing date. */
function stamp(iso: string): string {
  return DATE.format(new Date(iso));
}

/**
 * Everything true about this week that the grid itself cannot say.
 *
 * All of it was already reachable and none of it was shown: `submittedAt` and
 * `reviewedAt` now ride on `MyTimesheetDTO`, the engagement window has been on
 * `AssignmentDTO` the whole time and was used only for bounds arithmetic, the
 * holidays come from the same pure function the day rows already call, and the
 * lock horizon was a number the editor hardcoded into a sentence you only saw
 * *after* navigating into a week you could no longer edit.
 */
export function WeekContextRail({
  monday,
  today,
  rows,
  assignments,
  activeAssignments,
  onFileNil,
  fileNilDisabled,
}: {
  monday: Date;
  today: Date;
  rows: Row[];
  assignments: AssignmentDTO[];
  activeAssignments: AssignmentDTO[];
  /** Opens the editor's "submit with no hours" dialog for one project's period. */
  onFileNil: (row: Row) => void;
  fileNilDisabled: boolean;
}) {
  // A nil timesheet has no hours but is filed all the same (TIME-15).
  const withHours = rows.filter(
    (row) =>
      computeTotal(row.entries) > 0 ||
      (row.nilNote !== null && (row.status === "SUBMITTED" || row.status === "APPROVED"))
  );
  const owed = rows.filter(isOwed);
  const holidays = weekDays(monday)
    .map(nswPublicHoliday)
    .filter((holiday) => holiday !== null);
  const oldestEditable = addDays(startOfWeek(today), -7 * LOCK_AFTER_WEEKS);

  /**
   * Deliberately `useQuery`, not `useSuspenseQuery`, and deliberately not in the
   * route loader: the rail is context, and context must never be the reason the
   * hours grid is late. It streams in under a skeleton instead.
   */
  const recent = useQuery(recentTimesheetsQuery(monday));

  const byWeek = new Map<string, MyTimesheetRow[]>();
  for (const row of recent.data?.timesheets ?? []) {
    const list = byWeek.get(row.weekStart) ?? [];
    list.push(row);
    byWeek.set(row.weekStart, list);
  }

  return (
    <aside className="space-y-4" aria-label="Week context">
      <Card>
        <CardHeader title="This week" icon={Layers} />
        <div className="divide-y divide-line">
          {withHours.length === 0 && owed.length === 0 && (
            <p className="px-5 py-4 text-sm text-subtle">
              No hours logged yet. Pick a project on a day to start.
            </p>
          )}
          {withHours.map((row) => {
            const assignment = assignments.find((a) => a.projectId === row.projectId);
            const total = computeTotal(row.entries);
            return (
              <div key={rowKey(row)} className="px-5 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-text">
                      {assignment?.projectName ?? "Unknown project"}
                    </div>
                    <div className="truncate text-xs text-subtle">
                      {assignment?.clientName}
                      {/* Each half of a split week is its own timesheet (TIME-1). */}
                      {isHalfWeek(row) &&
                        ` · ${periodRange(fromYmd(row.periodStart), fromYmd(row.periodEnd))}`}
                    </div>
                  </div>
                  <div className="shrink-0 text-sm font-semibold tabular-nums text-text">
                    {formatHours(total)} h
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusBadge
                    status={
                      row.status === "NEW"
                        ? "Draft"
                        : row.status === "CHANGES_REQUESTED"
                          ? "Changes requested"
                          : TIMESHEET_STATUS_LABEL[row.status]
                    }
                  />
                </div>
                {row.nilNote && total === 0 && (
                  <p className="mt-1.5 text-xs text-muted">No hours · {row.nilNote}</p>
                )}
                {/* The contractor who filed it was the only person who could not
                    see when. Both stamps are new on `MyTimesheetDTO`. */}
                {(row.submittedAt ?? row.reviewedAt) && (
                  <div className="mt-1.5 space-y-0.5 text-micro text-subtle">
                    {row.submittedAt && <div>Submitted {stamp(row.submittedAt)}</div>}
                    {row.reviewedAt && (
                      <div>
                        {row.status === "APPROVED" ? "Approved" : "Reviewed"}{" "}
                        {stamp(row.reviewedAt)}
                        {row.reviewerName ? ` by ${row.reviewerName}` : ""}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {owed.map((row) => {
            const assignment = assignments.find((a) => a.projectId === row.projectId);
            return (
              <div key={rowKey(row)} className="px-5 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-text">
                      {assignment?.projectName ?? "Unknown project"}
                    </div>
                    <div className="truncate text-xs text-subtle">
                      {assignment?.clientName}
                      {isHalfWeek(row) &&
                        ` · ${periodRange(fromYmd(row.periodStart), fromYmd(row.periodEnd))}`}
                    </div>
                  </div>
                  <div className="shrink-0 text-sm font-semibold tabular-nums text-subtle">
                    0 h
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {/* The server's own deadline words (TIME-10), not a date worked out here. */}
                  <StatusBadge
                    status={
                      row.status === "CHANGES_REQUESTED"
                        ? "Changes requested"
                        : row.submission === "overdue"
                          ? "Overdue"
                          : row.submission === "due"
                            ? "Due"
                            : "Not filed"
                    }
                  />
                  <Button
                    variant="secondary"
                    size="xs"
                    disabled={fileNilDisabled}
                    onClick={() => onFileNil(row)}
                    className="shadow-none"
                  >
                    Submit with no hours
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <CardHeader title="Engagements" icon={CalendarRange} />
        <div className="divide-y divide-line">
          {activeAssignments.length === 0 && (
            <p className="px-5 py-4 text-sm text-subtle">
              You have no active engagements. Contact your Simplok admin.
            </p>
          )}
          {activeAssignments.map((assignment) => {
            // The narrower of the two windows is the one that actually binds, and
            // it is the one `engagementBounds` computes. Shown as dates rather than
            // "outside the project engagement dates", which named no date at all.
            const start = latest(assignment.assignmentStart, assignment.projectStart);
            const end = earliest(assignment.assignmentEnd, assignment.projectEnd);
            return (
              <div key={assignment.projectId} className="px-5 py-3">
                <div className="truncate text-sm font-medium text-text">
                  {assignment.projectName}
                </div>
                <div className="truncate text-xs text-subtle">{assignment.clientName}</div>
                <div className="mt-1.5 text-xs text-muted">
                  {start ? formatDay(new Date(`${start}T00:00:00Z`)) : "Open start"}
                  {" → "}
                  {end ? formatDay(new Date(`${end}T00:00:00Z`)) : "No end date"}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {holidays.length > 0 && (
        <Card>
          <CardHeader title="Public holidays" icon={PartyPopper} />
          <div className="space-y-1 px-5 py-4">
            {holidays.map((holiday) => (
              <div key={holiday.date} className="text-sm text-text">
                <span className="text-subtle">
                  {formatDay(new Date(`${holiday.date}T00:00:00Z`))} ·{" "}
                </span>
                {holiday.name}
              </div>
            ))}
            <p className="pt-1 text-micro text-subtle">
              NSW public holidays. Log hours only if you actually worked them.
            </p>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="Recent weeks" icon={History} />
        <div className="divide-y divide-line">
          {recent.isPending &&
            Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="px-5 py-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="mt-2 h-3 w-20" />
              </div>
            ))}
          {recent.isError && (
            <p className="px-5 py-4 text-sm text-subtle">
              Couldn&apos;t load your recent weeks.
            </p>
          )}
          {recent.isSuccess &&
            Array.from({ length: RECENT_WEEKS }, (_, i) =>
              addDays(monday, -7 * i)
            ).map((week) => {
              const key = ymd(week);
              const weekRows = byWeek.get(key) ?? [];
              const total = weekRows.reduce((sum, r) => sum + r.totalHours, 0);
              const isCurrent = key === ymd(monday);
              return (
                <Link
                  key={key}
                  to="/my-timesheet"
                  search={{ week: key }}
                  className={cn(
                    "flex items-center justify-between gap-2 px-5 py-2.5 transition-colors",
                    isCurrent ? "bg-accent-soft/40" : "hover:bg-surface-hover"
                  )}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-text">
                      Week {isoWeek(week)}
                      {isCurrent && (
                        <span className="ml-1.5 font-normal text-subtle">· open</span>
                      )}
                    </div>
                    <div className="truncate text-micro text-subtle">
                      {weekRange(week)}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm font-semibold tabular-nums text-text">
                      {weekRows.length > 0 ? `${formatHours(total)} h` : "—"}
                    </div>
                  </div>
                </Link>
              );
            })}
        </div>
      </Card>

      <Card>
        <CardHeader title="Editing window" icon={Info} />
        <div className="space-y-2 px-5 py-4 text-xs text-muted">
          <p>
            Weeks starting before{" "}
            <span className="font-medium text-text">{formatDay(oldestEditable)}</span> are
            locked — {LOCK_AFTER_WEEKS} weeks back. An admin can return a locked week to
            you.
          </p>
          <p>
            The week you are in is the latest you can fill. A week that hasn&apos;t started
            opens on its own Monday — you can still submit the current week before it
            ends.
          </p>
        </div>
      </Card>
    </aside>
  );
}

/** The later of two optional `ymd` dates — an open start loses to a real one. */
function latest(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

/** The earlier of two optional `ymd` dates — an open end loses to a real one. */
function earliest(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}
